import React, { useCallback, useEffect } from "react";
import { FocusContext, useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import SearchBar from "./SearchBar";
import OnScreenKeyboard, { KEY_TOKENS, FIRST_KEY_FOCUS } from "./OnScreenKeyboard";
import EPGFilteredEventsRow from "../EPGFilteredSectionRow";
import useSearchController, { SEARCH_VIEW } from "./useSearchController";
import { safeSetFocus } from "../utils/shared/focus";
import noResultsIcon from "../../assets/searchNoResults.svg";
import promptIcon from "../../assets/searchPrompt.svg";
import "./search.scss";

const EmptyPane = ({ icon, title, hint }) => (
    <div className="epgSearchEmpty">
        <img src={icon} alt="" className="epgSearchEmpty__icon" />
        <h3 className="epgSearchEmpty__title">{title}</h3>
        {hint && <p className="epgSearchEmpty__hint">{hint}</p>}
    </div>
);

// Dedicated full-view search: query bar + on-screen keyboard + live results.
// Results render through the existing (virtualized) filtered-events row.
const EPGSearchView = ({ onEnterPress, shouldApplyComcastStyles }) => {
    const { ref, focusKey } = useFocusable({ focusKey: "epg-search-view" });
    const { query, setQuery, results, submit, policy, view } = useSearchController();

    // Land focus on the keyboard when the view opens, so the remote has a starting point.
    useEffect(() => {
        const t = setTimeout(() => safeSetFocus(FIRST_KEY_FOCUS), 50);
        return () => clearTimeout(t);
    }, []);

    const onKeyPress = useCallback(
        (button) => {
            switch (button) {
                case KEY_TOKENS.SPACE:
                    setQuery((q) => `${q} `);
                    break;
                case KEY_TOKENS.DELETE:
                    setQuery((q) => q.slice(0, -1));
                    break;
                case KEY_TOKENS.SEARCH:
                    submit();
                    break;
                default:
                    setQuery((q) => q + button);
            }
        },
        [setQuery, submit]
    );

    const renderPane = () => {
        switch (view) {
            case SEARCH_VIEW.TOO_SHORT:
                return <EmptyPane icon={promptIcon} title="Keep typing to see results" />;
            case SEARCH_VIEW.RESULTS:
                return (
                    <EPGFilteredEventsRow
                        events={results}
                        title={`Results for search '${query.trim()}'`}
                        onEnterPress={onEnterPress}
                        shouldApplyComcastStyles={shouldApplyComcastStyles}
                        autoFocusOnResults={false}
                    />
                );
            case SEARCH_VIEW.EMPTY:
                return <EmptyPane icon={noResultsIcon} title="No Matching Results" hint="Check your spelling or try different keywords" />;
            default:
                return null;
        }
    };

    const pane = renderPane();

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="epgSearchView">
                <SearchBar query={query} />
                <div className="epgSearchView__body">
                    <OnScreenKeyboard mode={policy.mode} onKeyPress={onKeyPress} />
                    {pane && <div className="epgSearchView__results">{pane}</div>}
                </div>
            </div>
        </FocusContext.Provider>
    );
};

export default EPGSearchView;
